import { useCallback, useMemo, useRef } from "react";

import Actionbar from "src/components/common/actionbar/Authactionbar";
import { KioskSwitchButtonForm } from "src/components/common/form/KioskSwitchButtonForm";
import { Message } from "src/components/common/form/Message";
import useUiuxStore from "src/store/useUiuxStore";

import { useMouseContent } from "src/utils/mouse/mouseContent";
import { isUnsetWorkDateTime } from "src/utils/date/dateUtils";

import { AgGridReact } from "ag-grid-react";
import { Content_PRD3012_R01 } from "./Content_PRD3012_R01";
import { DetailTable_PRD3012_R01 } from "./DetailTable_PRD3012_R01";
import { PRD3012_R01DetailDto, PRD3012_R01HeaderDto, PRD3012_R01InspectDto, PRD3012_R01LotDto } from "./Dto_PRD3012_R01";
import { Info_PRD3012_R01 } from "./Info_PRD3012_R01";
import { ResultInfo_PRD3012_R01 } from "./ResultInfo_PRD3012_R01";
import { LotTable_PRD3012_R01 } from "./LotTable_PRD3012_R01";
import { Modal_PRD3012_R01 } from "./Modal_PRD3012_R01";
import { SearchSection_PRD3012_R01 } from "./SearchSection_PRD3012_R01";
import { Table_PRD3012_R01 } from "./Table_PRD3012_R01";
import { usePRD3012_R01Store } from "./Store_PRD3012_R01";
import { WorkTimeModal_PRD3012_R01 } from "./WorkTimeModal_PRD3012_R01";

const formatWorkDateTime = (value: string | undefined) => {
	if (!value) return '';
	const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
	if (!match) return '';
	return `${match[1]}-${match[2]}-${match[3]} ${match[4]}:${match[5]}:${match[6] ?? '00'}`;
};


export const Container_PRD3012_R01 = () => {

	const {
		editPRD3012_R01,
		initData, syncInitialData,
		isDetailMaximized, setIsDetailMaximized,
		PRD3012_R01Filter, setPRD3012_R01Filter,
	} = usePRD3012_R01Store();

	const { tabList, setMessage } = useUiuxStore();

	const RESIZER_LIST = {
		code: 'PRD3012_R01',
		type: 'width',
		storageKey: 'PRD3012_R01-split-width',
		min: 5,
		max: 80,
		default: 30
	} as const;

	const RESIZER_HEIGHT = {
		code: 'PRD3012_R01',
		type: 'height',
		storageKey: 'PRD3012_R01-info-split-height',
		min: 5,
		max: 80,
		default: 60
	} as const;

	const RESIZER_LOT = {
		code: 'PRD3012_R01_lot',
		type: 'width',
		storageKey: 'PRD3012_R01-info-result-split-width',
		min: 20,
		max: 80,
		default: 35
	} as const;

	const RESIZER_RESULT_LOT = {
		code: 'PRD3012_R01_result_lot',
		type: 'width',
		storageKey: 'PRD3012_R01-result-lot-split-width',
		min: 20,
		max: 80,
		default: 60
	} as const;

	const gridRef = useRef<AgGridReact<PRD3012_R01HeaderDto>>(null);
	const lotGridRef = useRef<AgGridReact<PRD3012_R01LotDto>>(null);
	const detailGridRef = useRef<AgGridReact<PRD3012_R01DetailDto>>(null);
	const inspectGridRef = useRef<AgGridReact<PRD3012_R01InspectDto>>(null);

	const containerRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const rightPanelRef = useRef<HTMLDivElement>(null);
	const infoAreaRef = useRef<HTMLDivElement>(null);
	const lotInfoRef = useRef<HTMLDivElement>(null);
	const resultLotContainerRef = useRef<HTMLDivElement>(null);
	const resultInfoRef = useRef<HTMLDivElement>(null);
	const isListResizing = useRef(false);
	const isHeightResizing = useRef(false);
	const isLotResizing = useRef(false);
	const isResultLotResizing = useRef(false);

	const workStartEndHeight = '50';

	const {
		splitValue: listSplitValue,
		handleMouseDown: handleListMouseDown,
	} = useMouseContent({
		resizer: RESIZER_LIST,
		isResizing: isListResizing,
		containerRef,
		infoRef: listRef
	});

	const {
		splitValue: heightSplitValue,
		handleMouseDown: handleHeightMouseDown,
	} = useMouseContent({
		resizer: RESIZER_HEIGHT,
		isResizing: isHeightResizing,
		containerRef: rightPanelRef,
		infoRef: infoAreaRef
	});

	const {
		splitValue: lotSplitValue,
		handleMouseDown: handleLotMouseDown,
	} = useMouseContent({
		resizer: RESIZER_LOT,
		isResizing: isLotResizing,
		containerRef: rightPanelRef,
		infoRef: lotInfoRef
	});

	const {
		splitValue: resultLotSplitValue,
		handleMouseDown: handleResultLotMouseDown,
	} = useMouseContent({
		resizer: RESIZER_RESULT_LOT,
		isResizing: isResultLotResizing,
		containerRef: resultLotContainerRef,
		infoRef: resultInfoRef
	});

	const {
		handlePrintClick,
		handleExcelClick,
		fetchData,
		defaultPRD3012_R01HeaderRow,
		defaultPRD3012_R01LotRow,
		defaultPRD3012_R01DetailRow,
		defaultPRD3012_R01InspectRow,
		initContainerData,
		handleDeleteClick,
		handleCreateClick,
		handleSaveClick,
		handleWorkTimeClick,
		handleFetchBomProductList,
		getProductWorkFieldHandlers,
		handleFetchProductResult,
		handleLotCreateClick,
		handleInspectConfirmClick,
		handleInspectDeleteClick,
	} = Content_PRD3012_R01({
		gridRef,
		lotGridRef,
		detailGridRef,
		inspectGridRef,
		tabList, setMessage,
		containerRef,
		isResizing: isListResizing,
	});

	const isWorkStartUnset = useCallback(() => {
		return isUnsetWorkDateTime(editPRD3012_R01?.workStartDt);
	}, [editPRD3012_R01?.workStartDt]);

	const isWorkEndUnset = useCallback(() => {
		return isUnsetWorkDateTime(editPRD3012_R01?.workEndDt);
	}, [editPRD3012_R01?.workEndDt]);

	useMemo(() => { syncInitialData(); }, []);

	const headerDefaultRow = useMemo(() => defaultPRD3012_R01HeaderRow(), [defaultPRD3012_R01HeaderRow]);
	const lotDefaultRow = useMemo(() => defaultPRD3012_R01LotRow(), [defaultPRD3012_R01LotRow]);
	const detailDefaultRow = useMemo(() => defaultPRD3012_R01DetailRow(), [defaultPRD3012_R01DetailRow]);
	const inspectDefaultRow = useMemo(() => defaultPRD3012_R01InspectRow(), [defaultPRD3012_R01InspectRow]);

	return <div className={`content-container`}>
		<Actionbar
			onFetchClick={fetchData}
			onCreateClick={handleCreateClick}
			onDeleteClick={handleDeleteClick}
			onSaveClick={handleSaveClick}
			onPrintClick={handlePrintClick}
			onExcelClick={handleExcelClick}
			onInitClick={initContainerData}
		/>
		<SearchSection_PRD3012_R01 onEnterKeyDown={() => { fetchData(); }} />
		<WorkTimeModal_PRD3012_R01 handleWorkTimeClick={handleWorkTimeClick} />
		<Modal_PRD3012_R01
			defaultPRD3012_R01HeaderRow={headerDefaultRow}
			inspectGridRef={inspectGridRef}
			defaultPRD3012_R01InspectRow={inspectDefaultRow}
			handleInspectConfirmClick={handleInspectConfirmClick}
			handleInspectDeleteClick={handleInspectDeleteClick}
		/>

		<div className="flex flex-1 p-[12px] gap-[4px] min-h-0" ref={containerRef}>
			<div className="h-full min-h-0" style={{ width: `${listSplitValue}%` }} ref={listRef}>
				<Table_PRD3012_R01
					gridRef={gridRef}
					handleCreateClick={handleCreateClick}
					defaultPRD3012_R01HeaderRow={headerDefaultRow}
				/>
			</div>

			<div
				className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300 shrink-0"
				onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleListMouseDown('LR', e.nativeEvent)}
			/>

			<div className="h-full flex flex-col gap-[4px] min-h-0 min-w-0" style={{ width: `${100 - listSplitValue}%` }} ref={rightPanelRef}>
				{!isDetailMaximized && (
					<>
						<div className="w-full max-w-full flex flex-1 gap-[4px] min-h-0" style={{ height: `${heightSplitValue}%` }}>
							<div
								className="h-full max-h-full flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] overflow-y-auto border border-gray-300 min-w-0"
								ref={lotInfoRef}
								style={{ width: `${lotSplitValue}%` }}
							>
								<Info_PRD3012_R01 editable={false}
									defaultPRD3012_R01HeaderRow={headerDefaultRow}
									handleFetchBomProductList={handleFetchBomProductList}
								/>
							</div>

							<div
								className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300 shrink-0"
								onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleLotMouseDown('LR', e.nativeEvent)}
							/>

							<div
								className="h-full max-h-full flex flex-col min-h-0 min-w-0"
								style={{ width: `${100 - lotSplitValue}%` }}
							>
								<div
									className="w-full flex items-center justify-start shrink-0 mb-[4px] gap-[8px] flex-nowrap overflow-x-auto"
									style={{ minHeight: `${workStartEndHeight}px` }}
								>
									<button
										className="relative w-[170px] min-w-[170px] h-full shrink-0 bg-blue-dark hover:bg-gray-400 text-white text-[16px] py-[1px] rounded-[4px]"
										onClick={() => handleWorkTimeClick(editPRD3012_R01?.productOrderNo || '', 'workStart')}
									>
										작업시작
										{!isWorkStartUnset() ? <br /> : ''}
										{!isWorkStartUnset() ? formatWorkDateTime(editPRD3012_R01?.workStartDt || undefined) : ''}
									</button>
									<button
										className="relative w-[170px] min-w-[170px] h-full shrink-0 bg-blue-dark hover:bg-gray-400 text-white text-[16px] py-[1px] rounded-[4px]"
										onClick={() => handleWorkTimeClick(editPRD3012_R01?.productOrderNo || '', 'workEnd')}
									>
										작업종료
										{!isWorkEndUnset() ? <br /> : ''}
										{!isWorkEndUnset() ? formatWorkDateTime(editPRD3012_R01?.workEndDt || undefined) : ''}
									</button>
									<div className="shrink-0">
										<KioskSwitchButtonForm
											title="라벨자동출력"
											name="labelPrintAuto"
											value={PRD3012_R01Filter.labelPrintAuto as 'Y' | 'N'}
											onChange={(name: string, value: 'Y' | 'N') => setPRD3012_R01Filter({ ...PRD3012_R01Filter, [name]: value })}
										/>
									</div>
								</div>
								<div className="w-full flex-1 min-h-0 flex gap-[4px]" ref={resultLotContainerRef}>
									<div
										className="h-full max-h-full flex flex-wrap content-start pt-[8px] pl-[8px] pr-[8px] overflow-y-auto border border-gray-300 min-w-0"
										ref={resultInfoRef}
										style={{ width: `${resultLotSplitValue}%` }}
									>
										<ResultInfo_PRD3012_R01
											defaultPRD3012_R01HeaderRow={headerDefaultRow}
											defaultPRD3012_R01LotRow={lotDefaultRow}
											getProductWorkFieldHandlers={getProductWorkFieldHandlers}
											handleFetchProductResult={handleFetchProductResult}
											handleLotCreateClick={handleLotCreateClick}
										/>
									</div>
									<div
										className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300 shrink-0"
										onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleResultLotMouseDown('LR', e.nativeEvent)}
									/>
									<div
										className="h-full max-h-full flex flex-col min-h-0 min-w-0"
										style={{ width: `${100 - resultLotSplitValue}%` }}
									>
										<LotTable_PRD3012_R01 lotGridRef={lotGridRef} defaultPRD3012_R01LotRow={lotDefaultRow} />
									</div>
								</div>
							</div>
						</div>

						<div
							className="h-[6px] bg-gray-100 cursor-row-resize hover:bg-gray-300"
							onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleHeightMouseDown('UD', e.nativeEvent)}
						/>
					</>
				)}
				<div
					ref={infoAreaRef}
					style={{ height: isDetailMaximized ? '100%' : `${100 - heightSplitValue}%` }}
				>
					<DetailTable_PRD3012_R01
						gridRef={detailGridRef}
						defaultPRD3012_R01DetailRow={detailDefaultRow}
						isMaximized={isDetailMaximized}
						onToggleMaximize={() => setIsDetailMaximized(!isDetailMaximized)}
					/>
				</div>
			</div>
		</div>
		<Message />
	</div>
}
